import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { apiRequest } from '@/api/client';

const FIRST_LOGIN_SESSION_KEY = 'client_pending_first_password_change';

function isFirstPasswordChangeFlow() {
  try {
    if (sessionStorage.getItem(FIRST_LOGIN_SESSION_KEY) === '1') return true;
  } catch {
    /* ignore */
  }
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.first_login === true;
  } catch {
    return false;
  }
}

const ChangePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isFirstLoginFlow, setIsFirstLoginFlow] = useState(() => isFirstPasswordChangeFlow());
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }
    setIsFirstLoginFlow(isFirstPasswordChangeFlow());
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('change_password_mismatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('change_password_min_length'));
      return;
    }

    if (!isFirstLoginFlow && !currentPassword.trim()) {
      toast.error(t('change_password_current_required'));
      return;
    }

    setLoading(true);

    const params = new URLSearchParams();
    params.set('old_password', isFirstLoginFlow ? '' : currentPassword.trim());
    params.set('new_password', password);

    const result = await apiRequest('/api/auth/client/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    }, { auth: 'client' });

    setLoading(false);

    if (!result.ok) {
      toast.error(result.error || t('change_password_error'));
      return;
    }

    try {
      sessionStorage.removeItem(FIRST_LOGIN_SESSION_KEY);
    } catch {
      /* ignore */
    }
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (u && typeof u === 'object') {
        u.first_login = false;
        localStorage.setItem('user', JSON.stringify(u));
      }
    } catch {
      /* ignore */
    }

    toast.success(t('change_password_success'));
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{
      background: 'linear-gradient(135deg, #e8f9ed 0%, #d4f1e0 50%, #c8eed8 100%)'
    }}>
      <div className="w-full max-w-md mx-auto px-6">
        <Card className="glass-card" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <CardHeader className="text-center py-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
                alt="AutoGo Icon" 
                className="h-12 w-12"
              />
              <span className="text-4xl font-bold" style={{color: '#0a3042'}}>AutoGo</span>
            </div>
            <CardTitle className="text-2xl font-bold" style={{color: '#0a3042'}}>
              {t('change_password')}
            </CardTitle>
            <CardDescription>
              {isFirstLoginFlow
                ? t('change_password_first_login_description')
                : t('change_password_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isFirstLoginFlow && (
                <div>
                  <Label htmlFor="currentPassword" className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('current_password')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t('ph_password_dots')}
                      required
                      autoComplete="current-password"
                      className="pl-10 pr-10 py-6 text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label={showCurrentPassword ? t('hide_password') : t('show_password')}
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('new_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('ph_password_dots')}
                    required
                    autoComplete="new-password"
                    className="pl-10 pr-10 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showNewPassword ? t('hide_password') : t('show_password')}
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('confirm_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('ph_password_dots')}
                    required
                    autoComplete="new-password"
                    className="pl-10 pr-10 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? t('hide_password') : t('show_password')}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full py-6 text-base font-semibold text-white"
                style={{backgroundColor: '#36e961'}}
              >
                {loading ? t('change_password_updating') : t('change_password_btn')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;